/**
 * §P.4 · Quién debe, y desde cuándo.
 *
 * Existe aparte del listado de recibos con `?unpaid=true` porque contesta otra
 * pregunta. Aquel dice **qué** recibos están sin cobrar; este dice **a quién llamo
 * hoy**: agrupa por familia, descuenta los cobros parciales y ordena **por lo más
 * antiguo primero**, que es el orden en que hay que llamar.
 *
 * `oldest_due_days` es lo que convierte el listado en una decisión:
 *
 * > «Debe 29 €» no dice nada. «Debe 29 € desde hace 74 días» es a quién llamar hoy.
 *
 * Y no lleva **ningún dato bancario**: el entrenador puede ver quién debe y cuánto
 * porque habla con las familias, nunca cómo se cobra. Por eso el listado se le
 * enseña y el detalle de un recibo no.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros } from '@/shared/ui/formato';
import {
  antiguedad,
  esDeudaVieja,
  type Moroso,
} from '@/club/facturacion/tipos';

export function Morosos({
  club,
  alAbrirPagador,
}: {
  club: string;
  alAbrirPagador: (pagadorId: string) => void;
}) {
  const morosos = useQuery({
    queryKey: ['morosos', club],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).debtors.get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Moroso[];
    },
  });

  const total = (morosos.data ?? []).reduce((s, m) => s + m.pending_cents, 0);

  return (
    <section>
      <header className="titulo-fila">
        <h1>Quién debe</h1>
      </header>

      <p className="tenue">
        Por familia y no por recibo, y de lo más antiguo a lo más reciente: es
        el orden en que hay que llamar.
      </p>

      {morosos.isPending && <Cargando filas={5} etiqueta="Cargando" />}

      {morosos.isError && (
        <PanelError
          problema={aProblema(morosos.error)}
          reintentar={() => void morosos.refetch()}
        />
      )}

      {morosos.data?.length === 0 && (
        <Vacio
          titulo="No debe nadie"
          explicacion="Todo lo emitido está cobrado. Cuando algo venza sin cobrar aparecerá aquí, con cuánto lleva esperando."
        />
      )}

      {morosos.data && morosos.data.length > 0 && (
        <>
          <div className="aviso" role="status">
            <strong>
              {morosos.data.length === 1
                ? '1 familia debe'
                : `${morosos.data.length} familias deben`}{' '}
              {euros(total)} en total.
            </strong>
          </div>

          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Paga</th>
                  <th>Por quién</th>
                  <th className="num">Recibos</th>
                  <th className="num">Debe</th>
                  <th>Desde</th>
                </tr>
              </thead>
              {/* Sin reordenar: viene por antigüedad, que es la decisión. */}
              <tbody>
                {morosos.data.map((m) => (
                  <tr
                    key={m.payer_id}
                    className={
                      esDeudaVieja(m.oldest_due_days) ? 'fila-aviso' : undefined
                    }
                  >
                    <td>
                      <button
                        type="button"
                        className="enlace-fila"
                        onClick={() => alAbrirPagador(m.payer_id)}
                      >
                        {m.payer_name}
                      </button>
                      {m.payer_email && (
                        <div className="tenue">{m.payer_email}</div>
                      )}
                    </td>
                    {/* De qué familia se habla: «Ruiz» no dice nada, «Lucía y Marco» sí. */}
                    <td className="tenue">
                      {m.students.length === 0 ? '—' : m.students.join(', ')}
                    </td>
                    <td className="num tenue">{m.invoice_count}</td>
                    <td className="num">
                      <strong>{euros(m.pending_cents)}</strong>
                    </td>
                    <td
                      className={
                        esDeudaVieja(m.oldest_due_days) ? undefined : 'tenue'
                      }
                    >
                      {antiguedad(m.oldest_due_days)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="tenue">
            Aquí no hay ningún dato bancario a propósito. Para anotar lo que
            entre, abre el recibo desde <strong>Recibos</strong>.
          </p>
        </>
      )}
    </section>
  );
}
